<template>
  <div class="fees" :class="{ 'fees--selected': selected }" @click="onClick">
    <div class="fees__content">
      <div class="fees__cost">
        <span v-if="isUpgrade" class="mr-[2px]">+</span>
        <span class="price__unit">{{ yuan.prefix }}</span>
        <span class="price__number">{{ yuan.value }}</span>
        <span v-if="displayCycle" class="price__cycle">
          /{{ displayCycle }}
        </span>
      </div>
      <div v-if="showPrice.value && !isUpgrade" class="original-price">
        {{ showPrice.prefix }}{{ showPrice.value
        }}<span v-if="displayCycle">/{{ displayCycle }} </span>
      </div>
    </div>
    <div v-if="priceByMonth.value" class="fees__unit">
      <div v-if="fees.buy_type === BUY_TYPE.SUBSCRIBE">
        {{ $t('xu-fei') }} {{ priceByMonth.string }}/{{ $t('yue') }}
      </div>
      <div v-else-if="!isUpgrade">
        {{ $t('di-zhi') }}
        {{ priceByMonth.prefix }} {{ priceByMonth.value.toFixed(1) }}/{{
          $t('yue')
        }}
      </div>
    </div>
    <div class="fees__name">
      {{ displayPackageName }}
      <strong v-if="isUpgrade" class="font-normal">
        {{ originalPrice.prefix }}{{ originalPrice.value }}
        <span v-if="displayCycle" class="price__cycle">
          /{{ displayCycle }}
        </span>
      </strong>
    </div>
    <span v-if="isShowTag" class="fees__tag">
      {{ $t('li-sheng') }} {{ discount }}
    </span>
    <span v-if="isShowHint" class="fees__tag">
      {{ $t('lian-xu-bao-yue-xiang-you-hui') }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { BUY_TYPE, type IFees } from '~/api/interface/rights'
import { formatPrice } from '~/utils/equity-helper'

const props = withDefaults(
  defineProps<{
    fees: IFees
    isUpgrade?: boolean
    selected: boolean
    discount?: string
  }>(),
  {}
)
const { $t } = useNuxtApp()

const emits = defineEmits(['on-select'])

const isShowHint = computed(() => {
  const fee = props.fees
  if (fee.buy_type === BUY_TYPE.SUBSCRIBE) {
    return fee.days === 30
      ? $t('lian-xu-bao-yue-xiang-you-hui')
      : $t('lian-xu-bao-nian-xiang-you-hui')
  }
  return false
})

const displayCycle = computed(() => {
  const fee = props.fees
  if (fee.buy_type === BUY_TYPE.SUBSCRIBE) {
    return fee.days === 30 ? $t('yue') : $t('nian')
  }

  return ''
})

const displayPackageName = computed(() => {
  const fee = props.fees
  if (fee.buy_type === BUY_TYPE.PAYMENT) {
    if (fee.days === 30) {
      return $t('yue-tao-can-30-tian')
    }

    if (fee.days === 365) {
      return $t('nian-tao-can-365-tian')
    }
  }
  if (fee.buy_type === BUY_TYPE.SUBSCRIBE) {
    if (fee.days === 30) {
      return $t('lian-xu-bao-yue')
    }

    if (fee.days === 365) {
      return $t('lian-xu-bao-nian')
    }
  }
  return ''
})

const isShowTag = computed(() => props.fees.days === 365 && props.discount)

const displayPrice = computed(() => {
  return (props.isUpgrade ? props.fees.upgrade_price : props.fees.price) || 0
})

const yuan = computed(() => formatPrice(displayPrice.value))

const originalPrice = computed(() => formatPrice(props.fees.price || 0))

const priceByMonth = computed(() =>
  formatPrice(
    props.fees.months > 1 ? displayPrice.value / (props.fees.months || 1) : 0
  )
)

const showPrice = computed(() => formatPrice(props.fees.show_price))

function onClick() {
  emits('on-select', props.fees)
}
</script>

<style lang="scss" scoped>
.fees {
  cursor: pointer;
  width: 150px;
  @apply relative;
  height: 100px;
  margin-top: 12px;
  border-radius: 8px;
  background-color: var(--back-0);
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid var(--back-4);

  .fees__content {
    display: flex;
    align-items: center;
    padding-left: 16px;
  }

  .original-price {
    font-size: 13px;
    line-height: 18px;
    text-decoration: line-through;
    text-align: center;
    opacity: 0.4;
    margin-left: 8px;
  }

  &--selected {
    border: 1px solid var(--primary-color);
    background-color: var(--purple-2);
  }
  .fees__name {
    font-size: 12px;
    line-height: 18px;
    font-weight: 400;
    text-align: left;
    display: block;
    padding-left: 15px;
  }
  &__unit {
    width: 100%;
    line-height: 24px;
    padding-left: 16px;
    font-size: 12px;
    color: var(--text-1);
    font-weight: var(--x-weight-medium, 500);
  }
  &__cost {
    text-align: center;
  }
  &__tag {
    font-size: 12px;
    padding: 2px 8px;
    line-height: 18px;
    @apply absolute text-white;
    left: -1px;
    top: -11px;
    font-weight: var(--x-weight-medium, 500);
    background-color: #ff5656;
    border-radius: 4px;
  }

  .price {
    line-height: 1.4;
    margin-bottom: 8px;
    height: 38px;
    &__number {
      font-size: 18px;
      color: var(--text-1);
      font-weight: var(--x-weight-medium, 500);
      line-height: 30px;
      margin-bottom: 8px;
    }
    &__unit {
      font-size: 18px;
      color: var(--text-1);
      font-weight: var(--x-weight-medium, 500);
    }
  }
}
</style>
